import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Download, Loader2, XCircle } from 'lucide-react';
import type { ExportSummary } from '@seller-portal/types';
import { EXPORT_LIMITS } from '@seller-portal/types';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { getErrorMessage } from '@/services/api';
import { ago, count } from '@/features/catalog/format';
import { downloadExportFile, exportsKey, useExports } from './api';

const RECENT_MS = 24 * 3_600_000;

function progress(e: ExportSummary): string {
  return e.rowsTotal
    ? `${count(e.rowsDone)} of ${count(e.rowsTotal)} rows`
    : `${count(e.rowsDone)} rows`;
}

function deletion(e: ExportSummary): string {
  if (!e.expiresAt) return '';
  const minutes = Math.max(0, Math.round((new Date(e.expiresAt).getTime() - Date.now()) / 60_000));
  if (e.firstDownloadedAt)
    return `deleted in ${minutes < 60 ? `${minutes} min` : `${Math.round(minutes / 60)} h`}`;
  return `kept ${EXPORT_LIMITS.unclaimedDays} days if not downloaded`;
}

/**
 * The exports tray (prototype toast, bottom right), in the shell so it survives moving between
 * pages: running exports with their progress, then ready ones with a download per file. It opens
 * when an export starts and when one finishes; it lists the last day's exports.
 */
export function ExportsTray({ sellerId }: { sellerId: string }) {
  const exports = useExports(sellerId);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recent = (exports.data ?? []).filter(
    (e) =>
      e.status === 'running' ||
      e.status === 'ready' ||
      Date.now() - new Date(e.createdAt).getTime() < RECENT_MS
  );
  const running = recent.filter((e) => e.status === 'running').length;
  const readyIds = recent
    .filter((e) => e.status === 'ready')
    .map((e) => e.id)
    .join(',');

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('merx:exports-open', show);
    return () => window.removeEventListener('merx:exports-open', show);
  }, []);
  // A newly finished export opens the tray.
  const [seenReady, setSeenReady] = useState<string | null>(null);
  useEffect(() => {
    if (
      seenReady !== null &&
      readyIds !== seenReady &&
      readyIds.split(',').some((id) => id && !seenReady.split(',').includes(id))
    )
      setOpen(true);
    setSeenReady(readyIds);
  }, [readyIds]);

  if (recent.length === 0) return null;

  const download = async (e: ExportSummary, partNo: number) => {
    setError(null);
    try {
      await downloadExportFile(sellerId, e.id, partNo);
      void queryClient.invalidateQueries({ queryKey: exportsKey(sellerId) });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[360px] max-w-[calc(100vw-32px)]">
      <div className="bg-ink overflow-hidden rounded-[10px] text-white shadow-[0_12px_32px_rgba(14,23,38,.32)]">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[12.5px]"
        >
          {running > 0 ? (
            <Loader2 size={15} className="animate-spin" aria-hidden="true" />
          ) : (
            <CheckCircle2 size={15} className="text-green" aria-hidden="true" />
          )}
          <span className="flex-1 font-semibold">
            {running > 0 ? `Exporting ${running}…` : `Exports · ${recent.length}`}
          </span>
          {open ? (
            <ChevronDown size={15} aria-hidden="true" />
          ) : (
            <ChevronUp size={15} aria-hidden="true" />
          )}
        </button>
        {open ? (
          <ul className="border-navy3 max-h-[50vh] overflow-y-auto border-t">
            {recent.map((e) => (
              <li key={e.id} className="border-navy3 border-b px-3.5 py-2.5 text-xs last:border-0">
                <div className="flex items-start gap-2">
                  {e.status === 'running' ? (
                    <Loader2 size={13} className="mt-0.5 animate-spin" aria-hidden="true" />
                  ) : e.status === 'ready' ? (
                    <CheckCircle2 size={13} className="text-green mt-0.5" aria-hidden="true" />
                  ) : (
                    <XCircle size={13} className="text-red mt-0.5" aria-hidden="true" />
                  )}
                  <div className="min-w-0 flex-1">
                    <b className="block truncate font-semibold">{e.label}</b>
                    <span className="text-nav-dim">
                      {e.status === 'running'
                        ? `Preparing · ${progress(e)}`
                        : e.status === 'ready'
                          ? `${count(e.rowsDone)} rows · ${e.files.length} file${e.files.length === 1 ? '' : 's'} · ${deletion(e)}`
                          : e.status === 'failed'
                            ? (e.error ?? 'Failed')
                            : `Deleted after download · ${ago(e.createdAt)}`}
                    </span>
                    {e.status === 'ready' ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {e.files.map((f) => (
                          <Button
                            key={f.partNo}
                            size="sm"
                            variant="cyan"
                            type="button"
                            onClick={() => void download(e, f.partNo)}
                          >
                            <Download size={12} aria-hidden="true" />{' '}
                            {e.files.length === 1
                              ? 'Download'
                              : `Part ${f.partNo} · ${count(f.rows)}`}
                          </Button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {error ? <p className="bg-red-bg text-red px-3.5 py-2 text-xs">{error}</p> : null}
      </div>
    </div>
  );
}
